import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { 
  MapPin, Upload, MessageSquare, Users, Star, ChevronDown, ChevronUp,
  Clock, Shield, Zap, ArrowRight, FileText, CheckCircle, Home,
  Mail, Handshake, Bell, Heart, Flame
} from 'lucide-react';

const KamerVerhurenPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { language } = useLanguage();
  const { toast } = useToast();
  const [location, setLocation] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [searcherCount] = useState(Math.floor(Math.random() * 200) + 1200);

  const handleCreateAd = () => {
    if (!isAuthenticated) {
      toast({
        title: language === 'nl' ? 'Login vereist' : 'Login required',
        description: language === 'nl' ? 'Log in om een advertentie te plaatsen' : 'Log in to place an ad',
        variant: 'destructive'
      });
      navigate('/login');
      return;
    }
    navigate('/verhuren');
  };

  const testimonials = language === 'nl' ? [
    {
      name: 'Ron',
      date: '17-03-2024',
      rating: 5,
      text: 'Binnen een paar dagen kreeg ik al 15 swipes op mijn kamer. Het profiel systeem maakt het makkelijk om de juiste huurder te kiezen. Fantastische ervaring!'
    },
    {
      name: 'Sophie',
      date: '05-05-2024',
      rating: 5,
      text: 'Swipe je woning heeft alle functies die ik nodig had. De profieltypen van kandidaten helpen enorm bij het maken van een keuze. Zeer aan te raden.'
    },
    {
      name: 'Thomas',
      date: '17-08-2024',
      rating: 5,
      text: 'In een week had ik mijn perfecte huurder gevonden. Het dating-concept werkt gewoon. Je ziet direct wie geïnteresseerd is en kunt kiezen.'
    }
  ] : [
    {
      name: 'Ron',
      date: '17-03-2024',
      rating: 5,
      text: 'Within a few days I got 15 swipes on my room. The profile system makes it easy to choose the right tenant. Fantastic experience!'
    },
    {
      name: 'Sophie',
      date: '05-05-2024',
      rating: 5,
      text: 'Swipe je woning has all the features I needed. The profile types of candidates help a lot in making a choice. Highly recommended.'
    },
    {
      name: 'Thomas',
      date: '17-08-2024',
      rating: 5,
      text: 'In a week I found my perfect tenant. The dating concept just works. You immediately see who is interested and can choose.'
    }
  ];

  const steps = language === 'nl' ? [
    {
      icon: Upload,
      title: 'Zet gratis je advertentie online',
      description: 'In drie eenvoudige stappen plaats je de advertentie. Voeg foto\'s, beschrijving en prijs toe.'
    },
    {
      icon: Heart,
      title: 'Ontvang swipes & matches',
      description: 'Wanneer iemand je woning liked, zie je direct hun profiel en bericht in je dashboard.'
    },
    {
      icon: Handshake,
      title: 'Kies je ideale huurder',
      description: 'Bekijk profielen, chat met kandidaten en kies de beste match voor je woning.'
    }
  ] : [
    {
      icon: Upload,
      title: 'List your property for free',
      description: 'In three simple steps you place the ad. Add photos, description and price.'
    },
    {
      icon: Heart,
      title: 'Receive swipes & matches',
      description: 'When someone likes your property, you immediately see their profile and message in your dashboard.'
    },
    {
      icon: Handshake,
      title: 'Choose your ideal tenant',
      description: 'View profiles, chat with candidates and choose the best match for your property.'
    }
  ];

  const faqs = language === 'nl' ? [
    {
      question: 'Wat kost het om te adverteren op Swipe je woning?',
      answer: 'Adverteren op Swipe je woning is volledig gratis. Je kunt onbeperkt advertenties plaatsen zonder kosten.'
    },
    {
      question: 'Hoe werkt het matching systeem?',
      answer: 'Wanneer een woningzoeker je woning liked, ontvang je hun profiel met informatie over profieltype, nationaliteit en een persoonlijk bericht. Je kunt vervolgens beslissen of je contact wilt opnemen.'
    },
    {
      question: 'Hoe snel vind ik een huurder?',
      answer: 'Gemiddeld vinden verhuurders binnen 10 dagen een geschikte huurder. Met 1.200+ actieve gebruikers krijgen de meeste advertenties al binnen 24 uur reacties.'
    },
    {
      question: 'Hoe weet ik of kandidaten serieus zijn?',
      answer: 'Alleen Premium gebruikers kunnen je woning liken. Dit zorgt ervoor dat je alleen serieuze kandidaten krijgt die actief op zoek zijn.'
    }
  ] : [
    {
      question: 'What does it cost to advertise on Swipe je woning?',
      answer: 'Advertising on Swipe je woning is completely free. You can place unlimited ads at no cost.'
    },
    {
      question: 'How does the matching system work?',
      answer: 'When a home seeker likes your property, you receive their profile with information about profile type, nationality and a personal message. You can then decide if you want to make contact.'
    },
    {
      question: 'How quickly will I find a tenant?',
      answer: 'On average, landlords find a suitable tenant within 10 days. With 1,200+ active users, most ads receive responses within 24 hours.'
    },
    {
      question: 'How do I know if candidates are serious?',
      answer: 'Only Premium users can like your property. This ensures you only get serious candidates who are actively looking.'
    }
  ];

  const features = language === 'nl' ? [
    {
      icon: Home,
      text: 'Plaats gratis en snel je kamer, studio of appartement'
    },
    {
      icon: Heart,
      text: 'Ontvang alleen swipes van serieuze kandidaten'
    },
    {
      icon: Bell,
      text: 'Direct notificaties bij nieuwe matches'
    }
  ] : [
    {
      icon: Home,
      text: 'List your room, studio or apartment for free and fast'
    },
    {
      icon: Heart,
      text: 'Only receive swipes from serious candidates'
    },
    {
      icon: Bell,
      text: 'Instant notifications for new matches'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-black" data-testid="kamer-verhuren-page">
      <Header />
      <Toaster />
      
      <main className="flex-1 pt-16">
        {/* Hero Section */}
        <section className="relative py-20 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-rose-600 via-pink-600 to-red-700"></div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-orange-500/30 via-transparent to-transparent"></div>
          
          <div className="relative max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left - Image */}
              <div className="relative hidden lg:block">
                <img 
                  src="https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800"
                  alt={language === 'nl' ? 'Gezellige woonkamer' : 'Cozy living room'}
                  className="rounded-2xl shadow-2xl w-full"
                />
                <div className="absolute -bottom-4 -right-4 bg-white rounded-2xl p-4 shadow-xl">
                  <div className="flex items-center gap-2">
                    <Heart className="w-6 h-6 text-rose-500 fill-rose-500" />
                    <span className="font-bold text-gray-900">{language === 'nl' ? '15+ matches/dag' : '15+ matches/day'}</span>
                  </div>
                </div>
              </div>

              {/* Right - Content */}
              <div className="text-center lg:text-left">
                <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md rounded-full px-5 py-2.5 mb-6">
                  <Flame className="w-5 h-5 text-yellow-400" />
                  <span className="text-white font-semibold text-sm tracking-wide">{language === 'nl' ? 'VOOR VERHUURDERS' : 'FOR LANDLORDS'}</span>
                </div>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4">
                  {language === 'nl' ? 'Verhuur je woning' : 'Rent out your property'}
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-400">
                    {language === 'nl' ? 'via Swipe je woning' : 'via Swipe je woning'}
                  </span>
                </h1>
                <p className="text-xl text-white/80 mb-8">
                  {language === 'nl' ? 'Ontvang direct reacties van gemotiveerde huurders' : 'Receive direct responses from motivated tenants'}
                </p>

                <Card className="bg-gray-900/90 backdrop-blur-sm border-0 shadow-2xl">
                  <CardContent className="p-6">
                    <h2 className="text-xl font-bold text-white mb-4">
                      {language === 'nl' ? 'Vind je nieuwe huurder in gemiddeld 10 dagen' : 'Find your new tenant in an average of 10 days'}
                    </h2>
                    
                    <div className="space-y-4">
                      <div className="relative">
                        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input
                          placeholder={language === 'nl' ? 'Plaats of postcode' : 'City or postal code'}
                          className="pl-12 h-12 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                          value={location}
                          onChange={(e) => setLocation(e.target.value)}
                        />
                      </div>

                      <p className="text-sm text-gray-400">
                        {language === 'nl' 
                          ? <>Momenteel zoeken <strong className="text-rose-400">{searcherCount.toLocaleString()}</strong> mensen via Swipe je woning een woning</>
                          : <>Currently <strong className="text-rose-400">{searcherCount.toLocaleString()}</strong> people are searching for a home via Swipe je woning</>
                        }
                      </p>

                      <Button 
                        onClick={handleCreateAd}
                        className="w-full h-14 bg-rose-500 hover:bg-rose-600 text-white font-bold text-lg rounded-xl"
                      >
                        <Heart className="w-5 h-5 mr-2" />
                        {language === 'nl' ? 'Gratis advertentie plaatsen' : 'Place free listing'}
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-12 border-b border-gray-800 px-4 bg-black">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              {features.map((feature, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-rose-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-7 h-7 text-rose-500" />
                  </div>
                  <p className="text-gray-300">{feature.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 3 Steps */}
        <section className="py-20 px-4 bg-black">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <Badge className="bg-rose-500/20 text-rose-400 border-0 mb-4">{language === 'nl' ? 'ZO WERKT HET' : 'HOW IT WORKS'}</Badge>
              <h2 className="text-2xl sm:text-3xl font-black text-white">
                {language === 'nl' ? 'In drie stappen jouw woning verhuren' : 'Rent out your property in three steps'}
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {steps.map((step, i) => (
                <Card key={i} className="border border-gray-800 bg-gray-900 text-center">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-rose-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <step.icon className="w-8 h-8 text-rose-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-3">{step.title}</h3>
                    <p className="text-gray-400">{step.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20 bg-gray-900 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <Badge className="bg-rose-500/20 text-rose-400 border-0 mb-4">REVIEWS</Badge>
              <h2 className="text-2xl sm:text-3xl font-black text-white">
                {language === 'nl' ? 'Dit zeggen verhuurders over Swipe je woning' : 'What landlords say about Swipe je woning'}
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((review, i) => (
                <Card key={i} className="bg-gray-800 border-0">
                  <CardContent className="p-6">
                    <div className="flex gap-0.5 mb-4">
                      {[...Array(review.rating)].map((_, j) => (
                        <Star key={j} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                      ))}
                    </div>
                    <p className="text-gray-300 mb-4">"{review.text}"</p>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-white">{review.name}</span>
                      <span className="text-sm text-gray-500">{review.date}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 px-4 bg-black">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-14">
              <Badge className="bg-rose-500/20 text-rose-400 border-0 mb-4">FAQ</Badge>
              <h2 className="text-2xl sm:text-3xl font-black text-white">
                {language === 'nl' ? 'Veelgestelde vragen' : 'Frequently asked questions'}
              </h2>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <div 
                  key={i} 
                  className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-5 text-left"
                  >
                    <span className="font-semibold text-white pr-4">{faq.question}</span>
                    {expandedFaq === i ? (
                      <ChevronUp className="w-5 h-5 text-rose-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-rose-500 flex-shrink-0" />
                    )}
                  </button>
                  {expandedFaq === i && (
                    <div className="px-5 pb-5 text-gray-400">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-24 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-rose-600 to-pink-600"></div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-white/10 to-transparent"></div>
          
          <div className="relative max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
              {language === 'nl' ? 'Klaar om je woning te verhuren?' : 'Ready to rent out your property?'}
            </h2>
            <p className="text-white/80 text-lg mb-8">
              {language === 'nl' ? 'Plaats gratis je advertentie en ontvang direct matches' : 'Place your free listing and receive matches instantly'}
            </p>
            <Button 
              onClick={handleCreateAd}
              className="bg-white text-rose-600 hover:bg-gray-100 h-16 px-12 font-bold text-lg rounded-full"
            >
              <Heart className="w-6 h-6 mr-2" />
              {language === 'nl' ? 'WONING PLAATSEN' : 'LIST PROPERTY'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default KamerVerhurenPage;
